import { useQuery } from "@tanstack/react-query";
import { StepUpResponse, TwoFactorDisableResponse, TwoFactorStatusResponse, type ErrorCode } from "@townsy/contracts";
import { API_BASE, apiFetch, isApiError } from "./client";

/**
 * Two-step sign-in (ADR-0039): the signed-in account's own state, the staff confirmation (step-up) and turning it off.
 * Set-up and sign-in codes go through Better Auth's client (auth/authClient.ts). The server decides everything.
 */
export const twoFactorEndpoints = {
  status: `${API_BASE}/me/two-factor`,
  stepUp: `${API_BASE}/me/two-factor/step-up`,
  disable: `${API_BASE}/me/two-factor/disable`,
};

export const twoFactorKeys = {
  all: ["two-factor"] as const,
  status: ["two-factor", "status"] as const,
};

export const fetchTwoFactorStatus = (signal?: AbortSignal) => apiFetch(twoFactorEndpoints.status, { schema: TwoFactorStatusResponse, signal });

/** The confirmation request itself never triggers another confirmation prompt. */
export const postStepUp = (code: string) => apiFetch(twoFactorEndpoints.stepUp, { method: "POST", body: { code }, schema: StepUpResponse, stepUp: false });

export const postTwoFactorDisable = (password: string, code: string) =>
  apiFetch(twoFactorEndpoints.disable, { method: "POST", body: { password, code }, schema: TwoFactorDisableResponse, stepUp: false });

export function useTwoFactorStatusQuery(enabled: boolean) {
  return useQuery({
    queryKey: twoFactorKeys.status,
    queryFn: ({ signal }) => fetchTwoFactorStatus(signal),
    enabled,
    staleTime: 15_000,
    retry: (count, err) => !(isApiError(err) && [401, 403].includes(err.status)) && count < 2,
  });
}

/** Plain messages for the confirmation and turn-off answers, by stable code. */
export function twoFactorApiMessage(err: unknown, fallback: string): string {
  if (!isApiError(err)) return fallback;
  const byCode: Partial<Record<ErrorCode, string>> = {
    VALIDATION_FAILED: "Enter the 6-digit code from your authenticator app.",
    MFA_CODE_INVALID: err.message,
    PASSWORD_INCORRECT: err.message,
    RATE_LIMITED: err.message || "Too many tries. Wait a minute and try again.",
    MFA_REQUIRED: err.message,
    FORBIDDEN: err.message,
    UNAUTHENTICATED: "Your session ended. Sign in again.",
  };
  return byCode[err.code] ?? (err.message || fallback);
}
